import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "~/hooks";
import { fetchAppDesign } from "~/slices/appDesignSlice";
import { fetchCustomizeSpecificationList } from "~/slices/customizeSpecificationListSlice";
import { fetchFieldDesign } from "~/slices/fieldDesignSlice";
import { fetchFunctionalRequirement } from "~/slices/functionalRequirementSlice";
import { fetchSystemProposal } from "~/slices/systemProposalSlice";

export type SectionStatus = "generated" | "generating" | "ready" | "locked" | "stale";

export type PageViewSection = {
  key: string;
  name: string;
  status: SectionStatus;
  documentType: string;
  canRegenerate: boolean;
};

const SECTION_DOCUMENT_TYPES = [
  "system-proposal",
  "functional-requirements",
  "app-design",
  "field-design",
  "customize-specification",
] as const;

const SECTION_NAMES: Record<string, string> = {
  "system-proposal": "システム概要",
  "functional-requirements": "主要機能一覧",
  "app-design": "作成するアプリ",
  "field-design": "作成するフィールド",
  "customize-specification": "実装するカスタマイズ",
};

// outputs が undefined の場合のフォールバック用安定参照（毎レンダリングで新オブジェクトを生成しない）
const EMPTY_OUTPUTS: Record<string, { id: string; isGenerating?: boolean | "" }> = {};

const PREREQUISITES: Record<string, string[]> = {
  "system-proposal": ["summary"],
  "functional-requirements": ["system-proposal"],
  "app-design": ["functional-requirements"],
  "field-design": ["app-design"],
  "customize-specification": ["field-design", "app-design"],
};

export const isAnyPrereqGenerating = (
  docType: string,
  outputs: Record<string, { id: string; isGenerating?: boolean | "" }>,
): boolean => {
  const prereqs = PREREQUISITES[docType] ?? [];
  return prereqs.some(
    (p) => outputs[p]?.isGenerating === true || isAnyPrereqGenerating(p, outputs),
  );
};

export const getCanRegenerate = (
  docType: string,
  status: SectionStatus,
  outputs: Record<string, { id: string; isGenerating?: boolean | "" }>,
): boolean => {
  if (status === "generating" || status === "locked") return false;
  return !isAnyPrereqGenerating(docType, outputs);
};

export const getSectionStatus = (
  docType: string,
  outputs: Record<string, { id: string; isGenerating?: boolean | "" }>,
): SectionStatus => {
  const output = outputs[docType];
  if (output?.isGenerating === true) return "generating";
  if (output?.id) return "generated";
  const prereqs = PREREQUISITES[docType] ?? [];
  const allMet = prereqs.every((p) => Boolean(outputs[p]?.id));
  return allMet ? "ready" : "locked";
};

export const computeStaleSet = (
  outputs: Record<string, { id: string; isGenerating?: boolean | "" }>,
  documentSources: Map<string, { type: string; id: string }[]>,
): Set<string> => {
  const staleSet = new Set<string>();

  // Pass 1: ソースIDミスマッチ検出
  for (const docType of SECTION_DOCUMENT_TYPES) {
    const docId = outputs[docType]?.id;
    if (!docId) continue;
    const sources = documentSources.get(docId);
    if (!sources) continue;
    for (const source of sources) {
      if (source.type === docType) continue;
      const currentId = outputs[source.type]?.id;
      if (currentId && currentId !== source.id) {
        staleSet.add(docType);
        break;
      }
    }
  }

  // Pass 2: カスケード伝播（stale の下流はすべて stale）
  // 前提: SECTION_DOCUMENT_TYPES の配列順序が依存関係の順序と一致していること
  // （system-proposal → functional-requirements → app-design → field-design → customize-specification）
  // 依存グラフが変わった場合はトポロジカルソートへの変更を検討すること
  let foundStale = false;
  for (const docType of SECTION_DOCUMENT_TYPES) {
    if (foundStale && outputs[docType]?.id) {
      staleSet.add(docType);
    }
    if (staleSet.has(docType)) {
      foundStale = true;
    }
  }

  return staleSet;
};

export const usePageViewData = () => {
  const dispatch = useAppDispatch();
  const outputs = useAppSelector((state) => state.auth.currentProject.outputs) ?? EMPTY_OUTPUTS;

  const systemProposalId = outputs["system-proposal"]?.id ?? "";
  const systemProposalGenerating = outputs["system-proposal"]?.isGenerating;
  const functionalRequirementsId = outputs["functional-requirements"]?.id ?? "";
  const functionalRequirementsGenerating = outputs["functional-requirements"]?.isGenerating;
  const appDesignId = outputs["app-design"]?.id ?? "";
  const appDesignGenerating = outputs["app-design"]?.isGenerating;
  const fieldDesignId = outputs["field-design"]?.id ?? "";
  const fieldDesignGenerating = outputs["field-design"]?.isGenerating;
  const customizeSpecId = outputs["customize-specification"]?.id ?? "";
  const customizeSpecGenerating = outputs["customize-specification"]?.isGenerating;

  useEffect(() => {
    if (systemProposalId && systemProposalGenerating !== true) {
      dispatch(fetchSystemProposal(systemProposalId));
    }

    if (functionalRequirementsId && functionalRequirementsGenerating !== true) {
      dispatch(fetchFunctionalRequirement(functionalRequirementsId));
    }

    if (appDesignId && appDesignGenerating !== true) {
      dispatch(fetchAppDesign(appDesignId));
    }

    if (fieldDesignId && fieldDesignGenerating !== true) {
      dispatch(fetchFieldDesign(fieldDesignId));
    }

    if (customizeSpecId && customizeSpecGenerating !== true) {
      dispatch(fetchCustomizeSpecificationList(customizeSpecId));
    }
  }, [
    dispatch,
    systemProposalId,
    systemProposalGenerating,
    functionalRequirementsId,
    functionalRequirementsGenerating,
    appDesignId,
    appDesignGenerating,
    fieldDesignId,
    fieldDesignGenerating,
    customizeSpecId,
    customizeSpecGenerating,
  ]);

  // stale 判定用: 各ドキュメントの sources を集約
  const spSources = useAppSelector((s) => s.systemProposalReducer.sources);
  const frSources = useAppSelector((s) => s.functionalRequirement.sources);
  const adSources = useAppSelector((s) => s.appDesign.sources);
  const fdSources = useAppSelector((s) => s.fieldDesign.sources);
  const csSources = useAppSelector((s) => s.customizeSpecificationList.sources);

  const documentSources = useMemo(() => {
    const map = new Map<string, { type: string; id: string }[]>();
    const entries: [string | undefined, { type: string; id: string }[]][] = [
      [outputs["system-proposal"]?.id, spSources],
      [outputs["functional-requirements"]?.id, frSources],
      [outputs["app-design"]?.id, adSources],
      [outputs["field-design"]?.id, fdSources],
      [outputs["customize-specification"]?.id, csSources],
    ];
    for (const [docId, sources] of entries) {
      if (docId && sources && sources.length > 0) {
        map.set(docId, sources);
      }
    }
    return map;
  }, [outputs, spSources, frSources, adSources, fdSources, csSources]);

  const staleSet = useMemo(
    () => computeStaleSet(outputs, documentSources),
    [outputs, documentSources],
  );

  const requirementData = useAppSelector((s) => s.appDesign.requirementData);
  const needsCustomize = useMemo(
    () => requirementData.some((r) => r.how.includes("カスタマイズ")),
    [requirementData],
  );

  const sections: PageViewSection[] = useMemo(
    () =>
      SECTION_DOCUMENT_TYPES.map((docType) => {
        const baseStatus = getSectionStatus(docType, outputs);
        const status = baseStatus === "generated" && staleSet.has(docType) ? "stale" : baseStatus;
        return {
          key: docType,
          name: SECTION_NAMES[docType] ?? docType,
          status,
          documentType: docType,
          canRegenerate: getCanRegenerate(docType, status, outputs),
        };
      }),
    [outputs, staleSet],
  );

  const filteredSections = needsCustomize
    ? sections
    : sections.filter((s) => s.documentType !== "customize-specification");

  const completedCount = filteredSections.filter(
    (s) => s.status === "generated" || s.status === "stale",
  ).length;
  const staleCount = filteredSections.filter((s) => s.status === "stale").length;
  const totalCount = filteredSections.length;
  const isAllComplete = completedCount === totalCount;
  const hasSummary = Boolean(outputs["summary"]?.id);
  const isSummaryGenerating = outputs["summary"]?.isGenerating === true;
  const isFieldDesignComplete = Boolean(outputs["field-design"]?.id);

  return {
    sections: filteredSections,
    needsCustomize,
    completedCount,
    staleCount,
    totalCount,
    isAllComplete,
    hasSummary,
    isSummaryGenerating,
    isFieldDesignComplete,
    outputs,
  };
};
